'use client';
import React, { useState, useEffect, useRef } from 'react';

import { useIndex } from '@/contexts/MarketDataContext';
import { X } from 'lucide-react';
import OptionChainContent from '@/app/option-chain/components/OptionChainContent';

// Display label map for index symbols
const INDEX_DISPLAY: Record<string, string> = {
  'NIFTY 50': 'NIFTY 50',
  'BANKNIFTY': 'BANK NIFTY',
  'MIDCPNIFTY': 'MIDCP NIFTY',
  'SENSEX': 'SENSEX',
  'FINNIFTY': 'FIN NIFTY',
  'GIFTNIFTY': 'GIFT NIFTY',
  'NIFTYMIDCAP150': 'MIDCAP 150',
};

// Map dashboard index keys → TradingView symbols
// These are the correct TradingView symbol identifiers for Indian indices
const INDEX_TO_TV_SYMBOL: Record<string, string> = {
  'NIFTY 50':       'NSE:NIFTY50',
  'BANKNIFTY':      'NSE:BANKNIFTY',
  'FINNIFTY':       'NSE:FINNIFTY',
  'SENSEX':         'BSE:SENSEX',
  'MIDCPNIFTY':     'NSE:MIDCPNIFTY',
  'NIFTYMIDCAP150': 'NSE:NIFTYMIDCAP150',
  'GIFTNIFTY':      'NSE:GIFT_NIFTY',
};

// Map dashboard index keys → OptionChainContent underlying keys
const INDEX_TO_UNDERLYING: Record<string, 'NIFTY' | 'BANKNIFTY' | 'FINNIFTY' | 'SENSEX' | 'MIDCPNIFTY' | 'MIDCAP150'> = {
  'NIFTY 50':       'NIFTY',
  'BANKNIFTY':      'BANKNIFTY',
  'FINNIFTY':       'FINNIFTY',
  'SENSEX':         'SENSEX',
  'MIDCPNIFTY':     'MIDCPNIFTY',
  'NIFTYMIDCAP150': 'MIDCAP150',
};

interface Props {
  selectedIndex?: string;
}

function TradingViewChart({ symbol }: { symbol: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Clear previous widget
    container.innerHTML = '';

    const containerId = `tv_chart_${symbol.replace(/[^a-zA-Z0-9]/g, '_')}_${Date.now()}`;
    container.id = containerId;

    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
    script.type = 'text/javascript';
    script.async = true;
    script.innerHTML = JSON.stringify({
      autosize: true,
      symbol: symbol,
      interval: '5',
      timezone: 'Asia/Kolkata',
      theme: 'dark',
      style: '1',
      locale: 'en',
      backgroundColor: 'rgba(15, 17, 23, 1)',
      gridColor: 'rgba(255, 255, 255, 0.06)',
      hide_top_toolbar: false,
      hide_legend: false,
      allow_symbol_change: true,
      save_image: true,
      calendar: false,
      hide_volume: false,
      support_host: 'https://www.tradingview.com',
    });

    container.appendChild(script);

    return () => {
      if (widgetRef.current) clearTimeout(widgetRef.current);
      if (container) container.innerHTML = '';
    };
  }, [symbol]);

  return (
    <div
      ref={containerRef}
      className="tradingview-widget-container"
      style={{ width: '100%', height: '100%', minHeight: 420 }}
    />
  );
}

export default function NiftyChartSection({ selectedIndex = 'NIFTY 50' }: Props) {
  const [showOptionChain, setShowOptionChain] = useState(false);

  const indexData = useIndex(selectedIndex);
  const value = indexData?.value ?? 0;
  const change = indexData?.change ?? 0;
  const pct = indexData?.pct ?? 0;
  const high = indexData?.high ?? 0;
  const low = indexData?.low ?? 0;
  const open = indexData?.open ?? 0;
  const prevClose = indexData?.prevClose ?? 0;
  const isUp = change >= 0;

  const displayName = INDEX_DISPLAY[selectedIndex] ?? selectedIndex;
  const tvSymbol = INDEX_TO_TV_SYMBOL[selectedIndex] ?? 'NSE:NIFTY50';

  return (
    <div className="card-elevated p-3 md:p-4 h-full flex flex-col">
      {/* Header row */}
      <div className="flex items-start justify-between mb-2 gap-2">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-foreground">{displayName}</h2>
          <div className="flex items-baseline gap-2 mt-0.5 flex-wrap">
            <span className="text-lg md:text-xl font-bold font-tabular text-foreground">
              {value > 0 ? value.toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '—'}
            </span>
            <span className={`text-xs font-semibold font-tabular ${isUp ? 'text-emerald-400' : 'text-red-400'}`}>
              {isUp ? '+' : ''}{change.toFixed(2)} ({isUp ? '+' : ''}{pct.toFixed(2)}%)
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => setShowOptionChain(true)}
            className="px-2.5 py-1 text-2xs font-semibold rounded-lg border border-primary/40 bg-primary/10 text-primary hover:bg-primary/20 transition-all duration-150 whitespace-nowrap"
          >
            Option Chain
          </button>
        </div>
      </div>

      {/* OHLC stats */}
      {value > 0 && (
        <div className="flex items-center gap-3 mb-2 text-2xs text-muted-foreground overflow-x-auto scrollbar-none pb-0.5">
          <span className="whitespace-nowrap">O: <span className="text-foreground font-tabular">{open.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></span>
          <span className="whitespace-nowrap">H: <span className="text-emerald-400 font-tabular">{high.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></span>
          <span className="whitespace-nowrap">L: <span className="text-red-400 font-tabular">{low.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></span>
          <span className="whitespace-nowrap">PC: <span className="text-foreground font-tabular">{prevClose.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></span>
        </div>
      )}

      {/* TradingView Advanced Chart Widget */}
      <div className="flex-1 min-h-0" style={{ minHeight: 420 }}>
        <TradingViewChart symbol={tvSymbol} />
      </div>

      {/* Option Chain Modal Overlay */}
      {showOptionChain && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm overflow-y-auto py-4 px-2"
          onClick={(e) => { if (e.target === e.currentTarget) setShowOptionChain(false); }}
        >
          <div className="relative w-full max-w-7xl bg-background rounded-xl border border-border shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <h2 className="text-sm font-semibold text-foreground">Option Chain — {displayName}</h2>
              <button
                onClick={() => setShowOptionChain(false)}
                className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150"
                aria-label="Close Option Chain"
              >
                <X size={16} />
              </button>
            </div>
            <div className="p-4">
              <OptionChainContent initialUnderlying={INDEX_TO_UNDERLYING[selectedIndex] ?? 'NIFTY'} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}